import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';

import { startEnviarAvisoSms, startObtenerDestinatariosAvisoSms } from '../../../../actions/profesionales';

const timezone = process.env.REACT_APP_TIMEZONE;

const LONGITUD_SMS_SIMPLE = 160;
const LONGITUD_MAXIMA_AVISO = 300;

// Los SMS salen en GSM7, que no admite á, í, ó, ú. Se conserva la ñ (n + virgulilla), que sí entra.
const quitarTildes = (texto) => texto.normalize('NFD').replace(/(?!̃)[̀-ͯ]/g, '').normalize('NFC');

const construirMensajeInicial = (nombreEmpresa, nombreProfesional) => quitarTildes(
    `Hola, somos ${nombreEmpresa}. ${nombreProfesional} ya no forma parte del equipo y no podra atender tu proxima cita. Contacta con nosotros para reorganizarla. Disculpa las molestias.`
);

const formatearFecha = (fecha) => moment(fecha).tz(timezone).format('DD/MM/YYYY HH:mm');

const describirEstadoDestinatario = ({ telefonoValido, yaAvisado }) => {
    if (yaAvisado) return 'Ya avisado';
    if (!telefonoValido) return 'Sin teléfono válido';
    return '';
};

const ListaClientes = ({ titulo, clientes, conMotivo = false }) => {
    if (!clientes?.length) return null;
    return (
        <div className='modal-aviso-sms__grupo'>
            <p className='modal-aviso-sms__titulo-grupo'>{`${titulo} (${clientes.length})`}</p>
            <ul className='modal-aviso-sms__lista'>
                {clientes.map((cliente, idx) => (
                    <li key={idx}>
                        {`${cliente.nombre} · ${cliente.telefono || 'sin teléfono'} · ${formatearFecha(cliente.fecha)}`}
                        {conMotivo && <span className='modal-aviso-sms__motivo'>{cliente.motivo}</span>}
                    </li>
                ))}
            </ul>
        </div>
    );
};

const ResultadoAviso = ({ resultado }) => (
    <>
        <p>{`SMS enviados: ${resultado.enviados.length}`}</p>
        <ListaClientes titulo='No se han podido enviar' clientes={resultado.fallidos} conMotivo />
        <ListaClientes titulo='Sin teléfono válido: avisar por otra vía' clientes={resultado.sinTelefonoValido} />
        <ListaClientes titulo='Ya estaban avisados' clientes={resultado.yaAvisados} />
    </>
);

export const ModalAvisoSmsProfesional = ({ profesional, modalOpened, setModalOpened }) => {
    const dispatch = useDispatch();
    const { colores } = useSelector(state => state.ui);

    const [vistaPrevia, setVistaPrevia] = useState(null);
    const [mensaje, setMensaje] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [resultado, setResultado] = useState(null);

    const profesionalId = profesional?._id;
    const nombreProfesional = profesional?.nombre || '';

    useEffect(() => {
        if (!modalOpened) return;
        setVistaPrevia(null);
        setResultado(null);

        const cargarVistaPrevia = async () => {
            const respuesta = await dispatch(startObtenerDestinatariosAvisoSms(profesionalId));
            if (!respuesta) return setModalOpened(false);
            setVistaPrevia(respuesta);
            setMensaje(construirMensajeInicial(respuesta.nombreEmpresa, nombreProfesional));
        };
        cargarVistaPrevia();
    }, [modalOpened, profesionalId, nombreProfesional, dispatch, setModalOpened]);

    const destinatarios = vistaPrevia?.destinatarios || [];
    const destinatariosAvisables = destinatarios.filter(({ telefonoValido, yaAvisado }) => telefonoValido && !yaAvisado);
    const mensajeValido = mensaje.trim().length > 0 && mensaje.length <= LONGITUD_MAXIMA_AVISO;
    const puedeEnviar = vistaPrevia?.smsHabilitados && destinatariosAvisables.length > 0 && mensajeValido && !enviando;

    const cerrar = () => {
        if (enviando) return;
        setModalOpened(false);
    };

    const enviar = async () => {
        if (!puedeEnviar) return;
        setEnviando(true);
        const respuesta = await dispatch(startEnviarAvisoSms(profesionalId, mensaje.trim()));
        setEnviando(false);
        if (!respuesta) return setModalOpened(false);
        setResultado(respuesta);
    };

    const renderCuerpo = () => {
        if (resultado) return <ResultadoAviso resultado={resultado} />;
        if (!vistaPrevia) return <p>Buscando citas pendientes...</p>;
        if (!vistaPrevia.smsHabilitados) return <p>La empresa no tiene los SMS habilitados.</p>;
        if (destinatarios.length === 0) return <p>{`${nombreProfesional} no tiene citas pendientes.`}</p>;

        return (
            <>
                <p>{`Clientes con citas pendientes: ${destinatarios.length}. Recibirán el SMS: ${destinatariosAvisables.length}.`}</p>
                <textarea
                    className='modal-aviso-sms__mensaje'
                    value={mensaje}
                    maxLength={LONGITUD_MAXIMA_AVISO}
                    onChange={(e) => setMensaje(e.target.value)}
                    disabled={enviando}
                />
                <p className='modal-aviso-sms__nota'>
                    {`${mensaje.length} caracteres${mensaje.length > LONGITUD_SMS_SIMPLE ? ' (más de un SMS por cliente)' : ''}. Evita las tildes: los SMS no las admiten.`}
                </p>
                <ul className='modal-aviso-sms__lista'>
                    {destinatarios.map((destinatario, idx) => (
                        <li key={idx}>
                            {`${destinatario.nombre} · ${destinatario.telefono || 'sin teléfono'} · ${formatearFecha(destinatario.fecha)}`}
                            <span className='modal-aviso-sms__motivo'>{describirEstadoDestinatario(destinatario)}</span>
                        </li>
                    ))}
                </ul>
            </>
        );
    };

    return (
        <div className={`modal-ylm modal-aviso-sms ${(modalOpened) ? 'active' : ''}`}>
            <div className="modal-bg"></div>
            <div className="modal-wrapper">
                <div className="modal-title">
                    <h3 style={{ color: colores.colorSecundario }}>{`Avisar por SMS a los clientes de ${nombreProfesional}`}</h3>
                </div>
                <div className="modal-body">
                    {renderCuerpo()}
                </div>
                <div className="modal-footer">
                    <div className="buttons">
                        {
                            !resultado &&
                            <div
                                className={`btn btn-confirm ${puedeEnviar ? '' : 'btn--deshabilitado'}`}
                                style={{ backgroundColor: colores.colorPrincipal }}
                                onClick={enviar}
                            >{enviando ? 'Enviando...' : `Enviar a ${destinatariosAvisables.length} clientes`}</div>
                        }
                        <div
                            className="btn btn-cancel"
                            style={{ backgroundColor: '#F2F2F2', color: '#393D40' }}
                            onClick={cerrar}
                        >{resultado ? 'Cerrar' : 'Cancelar'}</div>
                    </div>
                </div>
                <div
                    className="modal-close"
                    onClick={cerrar}
                ><span></span></div>
            </div>
        </div>
    );
};
